import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../utils/api';

const NotificationsModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeRead, setIncludeRead] = useState(true);

  const sanitizeNotificationHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    if (typeof DOMParser === 'undefined') return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const root = doc?.body;
    if (!root) return html;
    const allowedTags = new Set(['A', 'P', 'BR', 'STRONG', 'EM', 'U', 'UL', 'OL', 'LI']);

    const isSafeUrl = (value) => {
      const trimmed = value.trim();
      return (
        trimmed.startsWith('/') ||
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('mailto:')
      );
    };

    const walk = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (!allowedTags.has(node.tagName)) {
          const fragment = document.createDocumentFragment();
          while (node.firstChild) {
            fragment.appendChild(node.firstChild);
          }
          node.replaceWith(fragment);
          return;
        }

        [...node.attributes].forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          const isAnchorAllowedAttr = ['href', 'target', 'rel'].includes(attrName);
          if (node.tagName === 'A' && isAnchorAllowedAttr) {
            if (attrName === 'href' && !isSafeUrl(attr.value)) {
              node.removeAttribute('href');
            }
            return;
          }
          node.removeAttribute(attr.name);
        });

        if (node.tagName === 'A') {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }

      [...node.childNodes].forEach((child) => walk(child));
    };

    [...root.childNodes].forEach((child) => walk(child));
    return root.innerHTML || '';
  };

  const parseExtraData = (extraData) => {
    if (!extraData || typeof extraData !== 'string') {
      return {};
    }

    try {
      return JSON.parse(extraData);
    } catch (err) {
      const transcriptionMatch = extraData.match(/"transcription_id"\s*:\s*"([^"]+)"/);
      const taskMatch = extraData.match(/"task_id"\s*:\s*"([^"]+)"/);
      return {
        transcription_id: transcriptionMatch ? transcriptionMatch[1] : undefined,
        task_id: taskMatch ? taskMatch[1] : undefined
      };
    }
  };

  const getNotificationAction = (notification) => {
    const extraData = parseExtraData(notification.extra_data);
    const type = notification.notification_type;

    if (type === 'transcription_completed' || type === 'transcription_failed') {
      if (extraData.transcription_id && !extraData.hide_recording_link) {
        return {
          label: 'Открыть транскрибацию',
          route: `/transcription?open_history=1&transcription_id=${encodeURIComponent(extraData.transcription_id)}`
        };
      }
      return {
        label: 'Открыть Транскрибатор',
        route: '/transcription'
      };
    }

    if (type === 'log_analysis_completed' || type === 'log_analysis_failed') {
      return { label: 'Открыть DeepLog', route: '/deeplog' };
    }
    if (type === 'hr_analysis_completed' || type === 'hr_analysis_failed') {
      return { label: 'Открыть HR Агент', route: '/hr-agent' };
    }
    if (type === 'legal_analysis_completed' || type === 'legal_analysis_failed') {
      const route = extraData.task_id
        ? `/legal-assistant-v2?section=history&task=${encodeURIComponent(extraData.task_id)}`
        : '/legal-assistant-v2?section=history';
      return { label: 'Открыть AI Юрист', route };
    }
    if (type === 'risk_analysis_completed' || type === 'risk_analysis_failed') {
      const route = extraData.task_id
        ? `/legal-assistant-v2?section=history&task=${encodeURIComponent(extraData.task_id)}`
        : '/legal-assistant-v2?section=history';
      return { label: 'Открыть AI Юрист', route };
    }
    if (type === 'content_generation_completed' || type === 'content_generation_failed') {
      return { label: 'Открыть Контент-Завод', route: '/content-factory' };
    }
    if (type === 'ocr_completed' || type === 'ocr_failed') {
      return { label: 'Открыть AI Сканер', route: '/ocr-scanner' };
    }
    if (type === 'tech_writer_completed' || type === 'tech_writer_failed') {
      return { label: 'Открыть Технический писатель', route: '/tech-writer' };
    }
    if (type === 'web_analyst_completed' || type === 'web_analyst_failed') {
      const route = extraData.task_id
        ? `/web-analyst?task=${encodeURIComponent(extraData.task_id)}`
        : '/web-analyst';
      return { label: 'Открыть AI-Аналитик', route };
    }
    if (type === 'document_indexed' || type === 'document_index_failed') {
      return { label: 'Открыть Умный поиск', route: '/rag-v2' };
    }
    if (type === 'presentation_completed' || type === 'presentation_failed') {
      return { label: 'Открыть AI Презентации', route: '/ai-presentation' };
    }

    return null;
  };

  const isAdminBroadcastNotification = (notification) => {
    const extraData = parseExtraData(notification.extra_data);
    return (
      notification.notification_type === 'system_message' &&
      extraData?.source === 'admin_broadcast'
    );
  };

  const handleNotificationAction = async (notification) => {
    const action = getNotificationAction(notification);
    if (!action) return;

    try {
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }
    } catch (err) {
      console.error('Ошибка при пометке уведомления перед переходом:', err);
    }

    onClose();
    navigate(action.route);
  };

  // Загрузка уведомлений
  const loadNotifications = async (page = 1, includeReadNotifications = true) => {
    try {
      setLoading(true);
      setError(null);

      const skip = (page - 1) * 20;
      const response = await notificationAPI.getNotifications(skip, 20, includeReadNotifications);

      // Проверяем корректность ответа
      if (response && typeof response === 'object' && !response.detail) {
        setNotifications(Array.isArray(response.notifications) ? response.notifications : []);
        setTotal(typeof response.total === 'number' ? response.total : 0);
        setUnreadCount(typeof response.unread_count === 'number' ? response.unread_count : 0);
        setCurrentPage(page);
        setIncludeRead(includeReadNotifications);
      } else {
        // Если ответ некорректный, устанавливаем пустые значения
        setNotifications([]);
        setTotal(0);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Ошибка загрузки уведомлений:', err);
      setError('Не удалось загрузить уведомления');
      setNotifications([]);
      setTotal(0);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Пометить уведомление как прочитанное
  const markAsRead = async (notificationId) => {
    try {
      console.log('Отправка запроса на пометку уведомления как прочитанного:', notificationId);
      const updateData = { is_read: true };
      console.log('Данные запроса:', JSON.stringify(updateData));
      
      await notificationAPI.updateNotification(notificationId, updateData);
      console.log('Уведомление успешно помечено как прочитанное');

      // Обновляем локальное состояние
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );

      // Обновляем счетчик непрочитанных
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Ошибка при пометке уведомления как прочитанного:', err);
      console.error('Детали ошибки:', err.message, err.response);
    }
  };

  // Пометить все уведомления как прочитанные
  const markAllAsRead = async () => {
    try {
      console.log('Начало markAllAsRead');
      
      // Используем прямой fetch для диагностики
      const token = localStorage.getItem('accessToken');
      console.log('Токен есть:', !!token);
      
      const response = await fetch('/api/notifications/mark-all-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Статус ответа:', response.status);
      console.log('Заголовки ответа:', [...response.headers.entries()]);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('Текст ошибки:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Результат markAllRead:', result);

      // Обновляем локальное состояние
      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          is_read: true,
          read_at: new Date().toISOString()
        }))
      );

      setUnreadCount(0);
    } catch (err) {
      console.error('Ошибка при пометке всех уведомлений как прочитанных:', err);
      setError('Не удалось пометить уведомления как прочитанные: ' + err.message);
    }
  };

  // Удалить уведомление
  const deleteNotification = async (notificationId) => {
    const targetNotification = notifications.find(n => n.id === notificationId);
    if (targetNotification && isAdminBroadcastNotification(targetNotification)) {
      setError('Уведомления от администратора нельзя удалять');
      return;
    }

    if (!window.confirm('Удалить это уведомление?')) {
      return;
    }

    try {
      await notificationAPI.deleteNotification(notificationId);

      // Обновляем локальное состояние
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setTotal(prev => prev - 1);

      // Если удаленное уведомление было непрочитанным, уменьшаем счетчик
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Ошибка при удалении уведомления:', err);
      setError(err.message || 'Не удалось удалить уведомление');
    }
  };

  const deleteAllNotifications = async () => {
    if (!window.confirm('Удалить все события? Это действие нельзя отменить.')) {
      return;
    }

    try {
      await notificationAPI.deleteAllNotifications();
      await loadNotifications(1, includeRead);
      window.dispatchEvent(new Event('notificationUpdate'));
    } catch (err) {
      console.error('Ошибка при удалении всех уведомлений:', err);
      setError(err.message || 'Не удалось удалить все события');
    }
  };

  // Форматирование даты
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    let date;
    
    // Если дата содержит timezone info (+03:00 или Z) - парсим как есть
    if (dateStr.includes('+') || dateStr.includes('Z')) {
      date = new Date(dateStr);
    } else if (dateStr.includes('-', 10)) {
      // Содержит минус после даты (может быть отрицательный offset)
      date = new Date(dateStr);
    } else {
      // Дата без timezone - считаем её UTC и добавляем Z
      date = new Date(dateStr + 'Z');
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffInHours = diffMs / (1000 * 60 * 60);

    // Защита от некорректных дат
    if (isNaN(date.getTime())) {
      return '';
    }

    if (diffInHours < 0) {
      // Если дата в будущем (возможная ошибка timezone) - показываем "только что"
      return 'только что';
    } else if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffMs / (1000 * 60));
      return diffInMinutes <= 1 ? 'только что' : `${diffInMinutes} мин назад`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} ч назад`;
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Получение иконки для типа уведомления
  const getNotificationIcon = (type) => {
    const icons = {
      // Транскрибация
      'transcription_completed': '🎵',
      'transcription_failed': '❌',
      // DeepLog
      'log_analysis_completed': '📊',
      'log_analysis_failed': '⚠️',
      // HR Агент
      'hr_analysis_completed': '👔',
      'hr_analysis_failed': '❌',
      // AI Юрист
      'legal_analysis_completed': '⚖️',
      'legal_analysis_failed': '❌',
      // Контент-Завод
      'content_generation_completed': '✍️',
      'content_generation_failed': '❌',
      // OCR Сканер
      'ocr_completed': '📷',
      'ocr_failed': '❌',
      // Технический писатель
      'tech_writer_completed': '📝',
      'tech_writer_failed': '❌',
      // AI-Аналитик
      'web_analyst_completed': '🔍',
      'web_analyst_failed': '❌',
      // Умный поиск (индексация)
      'document_indexed': '📚',
      'document_index_failed': '❌',
      // Risk Analysis
      'risk_analysis_completed': '📈',
      'risk_analysis_failed': '❌',
      // AI Презентации
      'presentation_completed': '🎨',
      'presentation_failed': '❌',
      // Общие
      'report_generated': '📄',
      'system_message': 'ℹ️'
    };
    return icons[type] || '🔔';
  };

  // Загрузка уведомлений при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      loadNotifications(1, true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔔</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Уведомления</h2>
              <p className="text-sm text-gray-500">
                {unreadCount > 0 ? `${unreadCount} непрочитанных` : 'Все уведомления прочитаны'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                Прочитать все
              </button>
            )}
            {total > 0 && (
              <button
                onClick={deleteAllNotifications}
                className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
              >
                Удалить все события
              </button>
            )}
            <button
              onClick={() => setIncludeRead(!includeRead)}
              className={`px-3 py-1 text-sm rounded ${
                includeRead
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-blue-50 text-blue-600'
              }`}
            >
              {includeRead ? 'Только новые' : 'Все'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Содержимое */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-2 text-gray-600">Загрузка...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              {error}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">📭</div>
              <p>У вас пока нет уведомлений</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const action = getNotificationAction(notification);
                const isLockedAdminNotification = isAdminBroadcastNotification(notification);
                return (
                <div
                  key={notification.id}
                  className={`p-4 border rounded-lg ${
                    !notification.is_read
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-xl mt-1">
                        {getNotificationIcon(notification.notification_type)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 truncate">
                            {notification.title}
                          </h4>
                          {!notification.is_read && (
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        <div
                          className="text-sm text-gray-600 mb-2 break-words [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
                          dangerouslySetInnerHTML={{ __html: sanitizeNotificationHtml(notification.message) }}
                        />
                        <p className="text-xs text-gray-400">
                          {formatDate(notification.created_at)}
                        </p>
                        {action && (
                          <button
                            onClick={() => handleNotificationAction(notification)}
                            className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {action.label}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {!notification.is_read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          title="Пометить как прочитанное"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      )}
                      {!isLockedAdminNotification && (
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Удалить уведомление"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 011-1v1M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>

        {/* Пагинация */}
        {total > 20 && (
          <div className="border-t p-4 flex items-center justify-between">
            <button
              onClick={() => loadNotifications(currentPage - 1, includeRead)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
            >
              ← Назад
            </button>
            <span className="text-sm text-gray-600">
              Страница {currentPage} из {Math.ceil(total / 20)}
            </span>
            <button
              onClick={() => loadNotifications(currentPage + 1, includeRead)}
              disabled={currentPage >= Math.ceil(total / 20)}
              className="px-3 py-1 text-sm bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
            >
              Вперед →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsModal;
